if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelectorAll('.slider-dot'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          
          // Add vertical scrolling for thumbnail buttons
          this.initVerticalThumbnailScroll();
        }
        
        // Add dot navigation
        if (this.elements.dots.length > 0) {
          this.elements.dots.forEach((dot) => {
            dot.addEventListener('click', this.onDotClick.bind(this));
          });
        }
        
        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initVerticalThumbnailScroll() {
        const thumbnailSlider = this.elements.thumbnails;
        if (!thumbnailSlider) return;
        
        const prevButton = thumbnailSlider.querySelector('button[name="previous"]');
        const nextButton = thumbnailSlider.querySelector('button[name="next"]');
        const thumbnailList = thumbnailSlider.querySelector('[id^="Slider-Thumbnails"]');
        
        if (!prevButton || !nextButton || !thumbnailList) return;
        
        const scrollThumbnails = (direction) => {
          const thumbnailHeight = thumbnailList.querySelector('.thumbnail-list__item')?.offsetHeight || 0;
          const gap = 16; // 1rem gap
          const scrollAmount = thumbnailHeight + gap;
          
          const currentScroll = thumbnailList.scrollTop;
          const newScroll = direction === 'next' 
            ? currentScroll + scrollAmount 
            : currentScroll - scrollAmount;
          
          thumbnailList.scrollTo({
            top: newScroll,
            behavior: 'smooth'
          });
        };

        const updateButtonStates = () => {
          const isAtTop = thumbnailList.scrollTop <= 1;
          const isAtBottom = thumbnailList.scrollTop + thumbnailList.clientHeight >= thumbnailList.scrollHeight - 1;
          
          if (isAtTop) {
            newPrevButton.setAttribute('disabled', 'disabled');
          } else {
            newPrevButton.removeAttribute('disabled');
          }
          
          if (isAtBottom) {
            newNextButton.setAttribute('disabled', 'disabled');
          } else {
            newNextButton.removeAttribute('disabled');
          }
        };
        
        // Remove existing listeners and add new ones
        const newPrevButton = prevButton.cloneNode(true);
        const newNextButton = nextButton.cloneNode(true);
        prevButton.parentNode.replaceChild(newPrevButton, prevButton);
        nextButton.parentNode.replaceChild(newNextButton, nextButton);
        
        newPrevButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          scrollThumbnails('prev');
        });
        
        newNextButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          scrollThumbnails('next');
        });

        // Update button states on scroll
        thumbnailList.addEventListener('scroll', updateButtonStates);
        
        // Initial button state check
        updateButtonStates();
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updateDots(event.detail.currentPage);
      }
      
      onDotClick(event) {
        const slideIndex = parseInt(event.currentTarget.dataset.slideIndex);
        const slider = this.elements.viewer.slider;
        const slides = slider.querySelectorAll('[id^="Slide-"]');
        
        if (slides[slideIndex - 1]) {
          // Get the target slide
          const targetSlide = slides[slideIndex - 1];
          
          // Calculate scroll position accounting for margins
          const scrollPosition = targetSlide.offsetLeft - slider.offsetLeft;
          
          slider.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
          
          // Update active state
          this.updateDots(slideIndex);
        }
      }
      
      updateDots(currentPage) {
        if (!this.elements.dots || this.elements.dots.length === 0) return;
        this.elements.dots.forEach((dot, index) => {
          if (index + 1 === currentPage) {
            dot.classList.add('slider-dot--active');
          } else {
            dot.classList.remove('slider-dot--active');
          }
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
